/**
 * Render budget for the shared scene on a TV browser (TV_REVIVAL_PLAN.md 2.7).
 *
 * The scene itself is the phone's; the page chooses two things once at
 * startup so quality never jumps mid-putt: the phone's `reducedGraphics`
 * path (no shadow map, no MSAA, the smaller foliage atlas) or the full path,
 * and the device pixel ratio the canvas renders at.
 *
 * Default: FULL graphics. Measured 2026-09-25 on the production build in the
 * desktop app's Chrome 152 on an Apple M5 Mac, 1280x720 viewport rendering a
 * 1920x1080 buffer: 60 fps through the roll and review on the classic green
 * and on a realistic Links hole. `?graphics=reduced` (or a saved choice)
 * forces the phone's reduced path for a weak TV browser, `?graphics=full` the
 * full path; a browser reporting few cores or little memory starts reduced.
 *
 * TV browsers (2026-09-26, Kelly on a 2024 Samsung: "animation is jerkier ...
 * washed out or lower resolution" than the phone): a TV's GPU is far weaker
 * than its reported cores and memory suggest, so a TV browser starts on the
 * reduced path at up to 1080p, and every browser adapts: if a putt's frames
 * run slow, the next step down (full to reduced, then a lower pixel ratio)
 * applies between putts and is remembered for the next visit.
 *
 * Samsung and LG TVs (2026-10-01, Cole, when the Samsung beta was approved:
 * "it has the reduced graphics that worked for the lowest graphic apple TV"):
 * their GPUs measured about a third of the Apple TV 4K 1st gen's, the box
 * that needed reduced graphics at 0.35 of 1080p (appletv/src/quality.ts). So
 * the Samsung and LG apps and browsers START at that setting instead of
 * walking down to it one slow putt at a time, and may step lower still. No
 * new TV-app upload is needed: the apps are shells around this page.
 */
export type StageGraphics = 'full' | 'reduced';

const STORAGE_KEY = 'lagking-tv-graphics';
const FPS_KEY = 'lagking-tv-fps';

/**
 * Frame-rate cap for casting (2026-10-13). A PC browser showing this page
 * and casting or mirroring it to a TV renders every vsync (60, 120 or 144 Hz
 * on a gaming monitor) while a roll is moving; the cast encoder runs near
 * 30 fps and falls behind, and the TV shows the roll late and in lurches
 * (David, Discord: "lag casting from PC to TV", Sim 9 and Poker). The phone
 * has had a "Limit 3D to 30 fps" switch for the same reason since 2026-09-24;
 * the page had no way to ask for it. `?fps=30` caps moving frames at 30 and
 * is remembered like `?graphics`; `?fps=max` clears it. Smart TV browsers and
 * the Apple TV app are untouched (they drive the panel directly).
 */
export type FpsCap = 30 | null;

export function readFpsCap(search = window.location.search): FpsCap {
  try {
    const param = new URLSearchParams(search).get('fps');
    if (param === '30') {
      window.localStorage?.setItem(FPS_KEY, '30');
      return 30;
    }
    if (param === 'max' || param === '60') {
      window.localStorage?.removeItem(FPS_KEY);
      return null;
    }
    if (window.localStorage?.getItem(FPS_KEY) === '30') return 30;
  } catch {
    // Private mode or blocked storage: no cap.
  }
  return null;
}

/** The pacer delay for a cap (renderPacing.ts CAPPED_FRAME_DELAY_MS semantics). */
export function frameDelayForCap(cap: FpsCap): number {
  return cap === 30 ? 26 : 0;
}

function readOverride(): StageGraphics | null {
  try {
    const param = new URLSearchParams(window.location.search).get('graphics');
    if (param === 'full' || param === 'reduced') {
      window.localStorage?.setItem(STORAGE_KEY, param);
      return param;
    }
    const saved = window.localStorage?.getItem(STORAGE_KEY);
    if (saved === 'full' || saved === 'reduced') return saved;
  } catch {
    // Private mode or blocked storage: fall through to the default.
  }
  return null;
}

interface NavigatorWithMemory extends Navigator {
  deviceMemory?: number;
}

/** A television's browser or a TV-app shell (Samsung Tizen, LG webOS, and
 * the other smart-TV engines). */
export function isTvBrowser(userAgent: string): boolean {
  return /Tizen|SMART-TV|SmartTV|Web0S|webOS|NetCast|HbbTV|BRAVIA|VIDAA|Hisense|AFT[A-Z]|CrKey|GoogleTV|Android TV|AppleTV/i.test(userAgent);
}

/** Samsung (Tizen) and LG (webOS) engines: the weakest GPUs this page meets. */
export function isSmartTvEngine(userAgent: string): boolean {
  return /Tizen|Web0S|webOS|NetCast/i.test(userAgent);
}

function onSmartTv(): boolean {
  const shell = new URLSearchParams(window.location.search).get('shell');
  return shell === 'tizen' || shell === 'webos' || isSmartTvEngine(navigator.userAgent);
}

/** Where a Samsung or LG TV starts: the Apple TV 4K 1st gen's setting, as a
 *  share of a 1080p buffer on each side (672 x 378). */
export const SMART_TV_START_SCALE = 0.35;
/** The lowest adaptive step on those TVs, and everywhere else. */
export const SMART_TV_FLOOR_SCALE = 0.25;
export const DEFAULT_FLOOR_PIXEL_RATIO = 0.5;

/**
 * The canvas pixel ratio that renders `scale` of 1080p on each side, whatever
 * CSS viewport the TV reports (1920 x 1080 at 1, or 1280 x 720 at 1.5). Pure.
 */
export function pixelRatioForScale(scale: number, viewportWidth: number, viewportHeight: number): number {
  const viewportPixels = Math.max(1, viewportWidth * viewportHeight);
  return Math.round(scale * Math.sqrt((1920 * 1080) / viewportPixels) * 1000) / 1000;
}

/** The lowest pixel ratio adaptation may reach on this device. */
export function stageFloorPixelRatio(): number {
  return onSmartTv()
    ? pixelRatioForScale(SMART_TV_FLOOR_SCALE, window.innerWidth, window.innerHeight)
    : DEFAULT_FLOOR_PIXEL_RATIO;
}

function onTv(): boolean {
  const shell = new URLSearchParams(window.location.search).get('shell');
  return shell === 'tizen' || shell === 'webos' || isTvBrowser(navigator.userAgent);
}

export function detectStageGraphics(): StageGraphics {
  const override = readOverride();
  if (override) return override;
  if (onTv()) return 'reduced';
  const nav = navigator as NavigatorWithMemory;
  const memoryGb = nav.deviceMemory ?? 8;
  const cores = nav.hardwareConcurrency ?? 8;
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  // A TV browser or a very small laptop reports few cores and little memory:
  // start on the phone's reduced path there rather than dropping frames.
  return !reducedMotion && memoryGb >= 4 && cores >= 4 ? 'full' : 'reduced';
}

/** Cap the render resolution at 1080p-equivalent pixels and a 1.5x ratio: a
 *  4K TV browser at dpr 2 would otherwise push four times the fill rate for
 *  no visible gain at couch distance. */
export function stagePixelRatio(graphics: StageGraphics): number {
  const saved = readSavedPixelRatio();
  if (onSmartTv() && !graphicsPinnedByUrl()) {
    // A step remembered from before 2026-10-01 may sit above the new start.
    const start = pixelRatioForScale(SMART_TV_START_SCALE, window.innerWidth, window.innerHeight);
    return saved != null ? Math.min(saved, start) : start;
  }
  if (saved != null) return saved;
  // A TV aims for a full 1080p buffer (its browser may report a small CSS
  // viewport at dpr 2); elsewhere reduced stays at CSS pixels.
  const cap = onTv() ? 2 : graphics === 'full' ? 1.5 : 1;
  const maxPixels = 1080 * 1080 * (16 / 9);
  const viewportPixels = Math.max(1, window.innerWidth * window.innerHeight);
  const budgetRatio = Math.sqrt(maxPixels / viewportPixels);
  return Math.max(0.5, Math.min(window.devicePixelRatio || 1, cap, budgetRatio));
}

const PIXEL_RATIO_KEY = 'lagking-tv-pixel-ratio';

function graphicsPinnedByUrl(): boolean {
  return new URLSearchParams(window.location.search).has('graphics');
}

function readSavedPixelRatio(): number | null {
  try {
    if (graphicsPinnedByUrl()) return null;
    const saved = Number(window.localStorage?.getItem(PIXEL_RATIO_KEY));
    return Number.isFinite(saved) && saved >= 0.1 && saved <= 2 ? saved : null;
  } catch {
    return null;
  }
}

/** Remember an adaptive step so the next visit starts there. */
export function saveStageQuality(quality: StageQuality): void {
  try {
    window.localStorage?.setItem(STORAGE_KEY, quality.graphics);
    window.localStorage?.setItem(PIXEL_RATIO_KEY, String(quality.pixelRatio));
  } catch {
    // Blocked storage: the step still applies for this visit.
  }
}

export interface StageQuality {
  graphics: StageGraphics;
  pixelRatio: number;
}

/** Frames slower than this (under ~24 fps) during a roll call for a step down. */
export const SLOW_FRAME_MS = 42;

export function medianMs(samples: number[]): number | null {
  const sorted = samples.filter((v) => Number.isFinite(v) && v > 0).sort((a, b) => a - b);
  if (sorted.length < 10) return null;
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
}

/**
 * The next quality after a measured roll, or null to keep the current one.
 * Pure. Full graphics drops to reduced first (no shadows, no MSAA, the small
 * foliage atlas); reduced then lowers the pixel ratio in 20% steps to the
 * floor (0.5; lower on a Samsung or LG TV, stageFloorPixelRatio).
 * Never steps up: a TV that once struggled stays smooth.
 */
export function nextStageQuality(
  current: StageQuality,
  medianFrameMs: number | null,
  floor: number = DEFAULT_FLOOR_PIXEL_RATIO
): StageQuality | null {
  if (medianFrameMs == null || medianFrameMs <= SLOW_FRAME_MS) return null;
  if (current.graphics === 'full') return { graphics: 'reduced', pixelRatio: current.pixelRatio };
  if (current.pixelRatio <= floor) return null;
  const next = Math.max(floor, Math.round(current.pixelRatio * 0.8 * 100) / 100);
  return { graphics: 'reduced', pixelRatio: next };
}
